import {useClient, useDictionary} from '@adamlip/okeoke.client';
import {Button, Card, CardBody, Drawer, DrawerBody, DrawerContent} from '@heroui/react';
import SelectTableModal from 'components/Modals/SelectTableModal';
import {BasketContext} from 'contexts/BasketContext';
import useBasket from 'hooks/useBasket';
import L from 'leaflet';
import {useContext, useEffect, useRef, useState} from 'react';
import {MapContainer, Marker, Popup, TileLayer, useMap} from 'react-leaflet';
import {AppParamsContext} from 'contexts/AppParamsContext';
import BarSoftButton from 'components/_NewComponents/BarSoftButton';
import Icon from 'assets/icons';
import SendVibration from 'utils/Helpers-v1/SendVibration';

export default function LocationSelectorWithMap({isOpen, setIsOpen, isDismissable}) {
	const {setSelectedLocation, locations, selectedLocationUUID} = useClient();
	const {appParams, desktopView} = useContext(AppParamsContext);
	const {setIsUserSelectedLocation} = useContext(BasketContext);

	const {getValue} = useDictionary();
	const {resetTable, savedTable} = useBasket();
	const [centerCoords, setCenterCoords] = useState(null);
	const [selectTableOpen, setSelectTableOpen] = useState(false);
	const [selectedLocalLocationUUID, setSelectedLocalLocationUUID] = useState(null);
	const [isDragging, setIsDragging] = useState(false);
	const [startY, setStartY] = useState(0);
	const [offsetY, setOffsetY] = useState(0);
	const [lastScrollTop, setLastScrollTop] = useState(0);
	// Three states: "collapsed", "partial", "expanded"

	const [panelState, setPanelState] = useState('partial');
	const panelRef = useRef(null);
	const listRef = useRef(null);

	const handleLocationChange = (uuid, alsoClose) => {
		if (uuid !== savedTable?.locationUUID) {
			onClickResetTable();
		}
		SendVibration('light');

		setSelectedLocalLocationUUID(uuid);
		setIsUserSelectedLocation(true);
		setPanelState('collapsed');
		// const mapToUse = useMap();
		// if (mapToUse) {
		// }
		// getpopup by alt
		const popup = document.getElementsByName(`popup-${uuid}`);
		if (alsoClose) {
			setIsOpen(false);
			setIsUserSelectedLocation(true);
			setSelectedLocation(uuid);
		}
	};

	const openSelectTableModal = () => {
		setSelectTableOpen(true);
	};

	const onClickResetTable = () => {
		resetTable();
	};
	const LocationMarkers = () => {
		const map = useMap();

		const iconURL = appParams?.assets?.mapMarker || 'https://cdn.okeoke.io/global/assets/marker.png';

		const markerIcon = new L.Icon({
			iconUrl: iconURL,

			iconSize: [40, 40],
		});

		let markers = Object.values(locations || {})
			.filter((location) => location.enabled)
			.map((location) => {
				if (selectedLocalLocationUUID == location?.uuid) {
					map.flyTo([parseFloat(location.geolocation?.lat ?? 0), parseFloat(location.geolocation?.lng ?? 0)], 13, {
						duration: 1,
					});
				}
				return (
					<Marker id={`popup-${location.uuid}`} key={location.uuid} position={[parseFloat(location.geolocation?.lat ?? 0), parseFloat(location.geolocation?.lng ?? 0)]} icon={markerIcon}>
						<Popup
							eventHandlers={{
								click: (e) => {
									handleLocationChange(location.uuid);
									setTimeout(() => {
										map && map.flyTo([parseFloat(location.geolocation?.lat ?? 0), parseFloat(location.geolocation?.lng ?? 0)], 16);
									}, 600);
								},
							}}>
							<div className='p-1 flex flex-col gap-0 min-w-[150px]'>
								<div className='font-bold '>{location?.name}</div>

								<div className='text-xs pb-2'>{`${location.address?.city !== '' ? location.address?.city + ', ' : ''} ${location.address.zip} ${location.address.street} ${location.address.streetNumber}`}</div>

								<BarSoftButton forceCustom={true} color='primary' size='sm' radius='full' onPress={() => handleLocationChange(location.uuid, true)}>
									{getValue('app_v2_orderFromHere')}
								</BarSoftButton>
							</div>
						</Popup>
					</Marker>
				);
			});

		return markers;
	};

	const handleOpenChange = () => {
		if (isDismissable) setIsOpen(!isOpen);
		setPanelState('partial');
	};

	const onSave = () => {
		setIsOpen(false);
		setIsUserSelectedLocation(true);
		setSelectedLocation(selectedLocalLocationUUID);
	};

	useEffect(() => {
		if (selectedLocationUUID) {
			const selectedLocationIsInList = Object.values(locations || {}).find((location) => location.uuid === selectedLocationUUID);
			if (selectedLocationIsInList) {
				if (locations?.[selectedLocationUUID]?.geolocation?.lat && locations?.[selectedLocationUUID]?.geolocation?.lng) {
					setCenterCoords([locations?.[selectedLocationUUID]?.geolocation?.lat, locations?.[selectedLocationUUID]?.geolocation?.lng]);
				}
			} else {
				const firstLocationWithGeolocation = Object.values(locations || {}).find((location) => location.geolocation?.lat && location.geolocation?.lng);
				if (firstLocationWithGeolocation) {
					setCenterCoords([firstLocationWithGeolocation.geolocation.lat, firstLocationWithGeolocation.geolocation.lng]);
				}
			}
		} else {
			const firstLocationWithGeolocation = Object.values(locations || {}).find((location) => location.geolocation?.lat && location.geolocation?.lng);
			if (firstLocationWithGeolocation) {
				setCenterCoords([firstLocationWithGeolocation.geolocation.lat, firstLocationWithGeolocation.geolocation.lng]);
			}
		}
	}, [locations]);

	useEffect(() => {
		if (locations?.[selectedLocalLocationUUID]?.geolocation?.lat && locations?.[selectedLocalLocationUUID]?.geolocation?.lng) {
			setCenterCoords([locations?.[selectedLocalLocationUUID]?.geolocation?.lat, locations?.[selectedLocalLocationUUID]?.geolocation?.lng]);
		}
	}, [selectedLocalLocationUUID]);
	const handleTouchStart = (e) => {
		if (listRef.current && listRef.current.contains(e.target) && panelState === 'expanded') {
			// Allow normal scrolling when touching the list in expanded mode
			if (listRef.current.scrollTop > 0) {
				return;
			}
		}

		setIsDragging(true);
		setStartY(e.touches[0].clientY);
	};

	const handleMouseDown = (e) => {
		if (listRef.current && listRef.current.contains(e.target) && panelState === 'expanded') {
			// Allow normal scrolling when clicking the list in expanded mode
			if (listRef.current.scrollTop > 0) {
				return;
			}
		}

		setIsDragging(true);
		setStartY(e.clientY);
	};

	const handleTouchMove = (e) => {
		if (!isDragging) return;
		const currentY = e.touches[0].clientY;
		const diff = startY - currentY;
		setOffsetY(diff);

		// Prevent default to stop scrolling while dragging
		e.preventDefault();
	};

	const handleMouseMove = (e) => {
		if (!isDragging) return;
		const currentY = e.clientY;
		const diff = startY - currentY;
		setOffsetY(diff);
	};

	const handleDragEnd = () => {
		if (!isDragging) return;
		setIsDragging(false);

		// Determine which state to snap to based on current state and drag direction
		if (panelState === 'partial') {
			if (offsetY > 80) {
				setPanelState('expanded');
			} else if (offsetY < -80) {
				setPanelState('collapsed');
			}
		} else if (panelState === 'collapsed') {
			if (offsetY > 80) {
				setPanelState('partial');
			}
		} else if (panelState === 'expanded') {
			if (offsetY < -80) {
				setPanelState('partial');
			}
		}

		setOffsetY(0);
	};

	// Toggle panel state with buttons
	const togglePanelUp = () => {
		if (panelState === 'collapsed') setPanelState('partial');
		else if (panelState === 'partial') setPanelState('expanded');
	};

	const togglePanelDown = () => {
		if (panelState === 'expanded') setPanelState('partial');
		else if (panelState === 'partial') setPanelState('collapsed');
	};

	// Handle scroll events on the location list
	const handleListScroll = (e) => {
		if (panelState !== 'partial') return;

		const currentScrollTop = e.target.scrollTop;

		// If scrolling up and near the top of the list, expand the panel
		if (currentScrollTop < lastScrollTop && currentScrollTop < 50) {
			setPanelState('expanded');
		}

		setLastScrollTop(currentScrollTop);
	};

	useEffect(() => {
		const handleMouseUp = () => handleDragEnd();
		const handleTouchEnd = () => handleDragEnd();

		window.addEventListener('mouseup', handleMouseUp);
		window.addEventListener('touchend', handleTouchEnd);

		return () => {
			window.removeEventListener('mouseup', handleMouseUp);
			window.removeEventListener('touchend', handleTouchEnd);
		};
	}, [isDragging, offsetY, panelState]);

	// Add scroll event listener to the list
	useEffect(() => {
		const listElement = listRef.current;
		if (listElement) {
			listElement.addEventListener('scroll', handleListScroll);

			return () => {
				listElement.removeEventListener('scroll', handleListScroll);
			};
		}
	}, [panelState]);

	// Calculate panel height based on state
	const getPanelHeight = () => {
		if (isDragging) {
			// During dragging, adjust height based on current state and offset
			if (panelState === 'collapsed') {
				return `calc(200px + ${offsetY}px)`;
			} else if (panelState === 'partial') {
				return `calc(50% + ${offsetY}px)`;
			} else {
				return `calc(90% + ${offsetY}px)`;
			}
		}

		// Static heights for each state
		if (panelState === 'collapsed') return '200px';
		if (panelState === 'expanded') return '90%';
		return '50%'; // partial
	};
	if (locations == null) return null;
	return (
		<>
			<SelectTableModal isOpen={selectTableOpen} setIsOpen={setSelectTableOpen} onClose={() => setIsOpen(false)} />
			<Drawer
				isOpen={isOpen}
				isDismissable={isDismissable}
				// onOpenChange={handleOpenChange}
				size='full'
				placement='bottom'
				hideCloseButton={true}
				classNames={{
					base: 'overflow-hidden flex flex-col ',
					closeButton: 'z-50 bg-primary text-primary-foreground',
				}}>
				<DrawerContent className={`${appParams?.styles?.fonts?.capitalize ? 'font-extrabold uppercase' : ''}`}>
					{(onClose) => (
						<>
							<DrawerBody className='grow flex flex-col gap-2 overflow-hidden overflow-y-scroll px-0 pt-0 pb-0'>
								<div className='w-full rounded-general overflow-hidden h-full shrink-0 z-[30]'>
									{centerCoords && (
										<MapContainer center={centerCoords} zoom={10} scrollWheelZoom={true} id='map-container' className='w-full h-full '>
											<TileLayer attribution='' url={'https://api.mapbox.com/styles/v1/lazslov/cknu4mmz30lux17o0su9fwkef/tiles/256/{z}/{x}/{y}@2x?access_token=pk.eyJ1IjoibGF6c2xvdiIsImEiOiJja251NGdwaG0wOWV2Mm9xcTl3dmwwODRxIn0.cs7SkE6IVIwDQo0n0fA4JQ'} />
											<LocationMarkers />
										</MapContainer>
									)}
								</div>
								<div
									ref={panelRef}
									className={`absolute bottom-0 left-0 right-0 bg-background overflow-hidden flex flex-col gap-0 shadow-lg  z-[500] rounded-t-xl shadow-lg transition-all duration-300 ease-in-out ${isDragging ? 'transition-none' : ''}`}
									style={{
										height: getPanelHeight(),
									}}>
									<div className='absolute right-3 top-3 flex gap-2 z-[9999]'>
										{panelState !== 'expanded' && (
											<Button variant='flat' color='default' size='sm' isIconOnly onPress={togglePanelUp}>
												<div className='w-6 h-6 rotate-[-90deg]'>
													<Icon name='right' className='stroke-current' />
												</div>
											</Button>
										)}
										{panelState !== 'collapsed' && (
											<Button variant='flat' color='default' size='sm' isIconOnly onPress={togglePanelDown}>
												<div className='w-6 h-6 rotate-90'>
													<Icon name='right' className='stroke-current' />
												</div>
											</Button>
										)}
									</div>
									<div className='h-10 flex items-center justify-center cursor-grab active:cursor-grabbing' onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}>
										<div className='w-10 h-1 bg-foreground/50 rounded-full'></div>
									</div>

									<div className='px-4 pb-2 ' onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}>
										<h3 className='text-lg font-semibold'>{getValue('app_v2_locationSelector_title')}</h3>
										<p className='text-sm text-muted-foreground'>{panelState === 'expanded' ? getValue('app_v2_locationSelector_dragDown') : getValue('app_v2_locationSelector_dragUp')}</p>
									</div>

									{panelState !== 'collapsed' && (
										<div ref={listRef} className='overflow-hidden items-start justify-start pb-2 flex flex-col gap-2 px-2  grow'>
											<div className='overflow-y-auto grow items-start justify-start pb-10 flex flex-col gap-2 px-2 w-full '>
												<div className='flex flex-col gap-2 w-full items-center justify-center'>
													{appParams?.order?.showTableSelector && (
														<div className='flex-row w-full flex gap-2  overflow-hidden shrink-0'>
															<BarSoftButton className='!shrink-0 max-w-[200px]' fullWidth color='primary' onPress={openSelectTableModal}>
																{savedTable?.name == null && <div className='location-name select-table-text'>{getValue('app_v2_selectTable')}</div>}
																{savedTable?.name != null && <div className='location-address font-smallest'>{`${'kiválasztva: ' + savedTable?.name}`}</div>}
															</BarSoftButton>
															{savedTable?.name != null && (
																<BarSoftButton className=' !shrink-0' onPress={onClickResetTable}>
																	{savedTable?.name != null && <div className={''}>{getValue('app_v2_resetTable')}</div>}
																</BarSoftButton>
															)}
														</div>
													)}
												</div>
												{Object.values(locations || {}).filter((location) => location.enabled === 1).length < 1 && <div className='font-bold text-waring'>{getValue('app_v2_no_locations')}</div>}
												{Object.values(locations || {})
													.filter((location) => location.enabled === 1)
													.map((location) => (
														<Card
															shadow='none'
															isPressable
															key={location.uuid}
															className={`${appParams?.styles?.fonts?.capitalize ? 'font-extrabold uppercase' : ''} shrink-0 gap-1 rounded-general ${location.uuid === selectedLocalLocationUUID ? ' border-primary bg-primary/20 text-primary' : 'border-border'} border-2 w-full`}
															onPress={() => {
																handleLocationChange(location.uuid);
															}}>
															<CardBody className='w-full flex flex-row gap-2 items-center justify-between'>
																<div className='text-current w-[calc(100%-83px)]'>
																	<div className='font-bold'>{location.name}</div>
																	<div className='text-xs'>{`${location.address?.city !== '' ? location.address?.city + ', ' : ''} ${location.address.zip} ${location.address.street} ${location.address.streetNumber}`}</div>
																	{location?.openState?.forceClosedUntil != null && <div className='text-xs'>{`${getValue(`app_v2_location_pause_reason_${location?.openState?.forceClosedUntilReason}`)} ${getValue('app_v2_location_pause_text')} ${location?.openState?.forceClosedUntil}`}</div>}
																</div>
																<div className='flex flex-row gap-2 items-center'>
																	{location?.openState?.forceClosedUntil != null && (
																		<BarSoftButton color='warning' className='uppercase'>
																			{getValue('app_v2_location_pause')}
																		</BarSoftButton>
																	)}
																	{location?.openState?.forceClosedUntil == null && (
																		<div className={`uppercase p-2 font-bold rounded-full text-xs ${location?.openState?.open ? 'bg-success text-success-foreground' : 'bg-danger text-danger-foreground'}`}>
																			{location?.openState?.open ? getValue('app_v2_location_open') : getValue('app_v2_location_closed')}
																		</div>
																	)}
																</div>
															</CardBody>
														</Card>
													))}
											</div>
										</div>
									)}
									<div className='flex flex-col gap-2 items-center justify-center shrink-0 w-full h-min px-2 pb-2'>
										{isDismissable && (
											<BarSoftButton color='secondary' fullWidth forceCustom={true} variant='bordered' onPress={() => setIsOpen(false)} className={`${appParams?.styles?.fonts?.capitalize ? 'font-extrabold uppercase' : ''} `}>
												{getValue('app_v2_locationSelector_cancel')}
											</BarSoftButton>
										)}
										<BarSoftButton color='primary' fullWidth forceCustom={true} onPress={() => onSave()} isDisabled={selectedLocalLocationUUID == null} className={`${appParams?.styles?.fonts?.capitalize ? 'font-extrabold uppercase' : ''} `}>
											{`${locations?.[selectedLocalLocationUUID]?.name || getValue('app_v2_select_location')}`}
										</BarSoftButton>
									</div>
								</div>
							</DrawerBody>
						</>
					)}
				</DrawerContent>
			</Drawer>
		</>
	);
}
